import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { manualReportsApi as api } from "../../../services/manualReportsApi";
import "./manualReports.css";

const DEFAULT_REPORT = {
  client_name: "",
  report_title: "",
  assessment_type: "Web Application Penetration Test",
  start_date: "",
  end_date: "",
  testers: "",
  scope_summary: "",
  testing_notes: "",
  methodology_notes: "",
  report_notes: "",
};

function createDefaultReport() {
  return { ...DEFAULT_REPORT };
}

export default function ManualReportsPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [formState, setFormState] = useState(createDefaultReport);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    loadReports();
  }, []);

  async function loadReports() {
    try {
      setIsLoading(true);
      setError("");
      const data = await api.listReports();
      setReports(data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCreateReport(event) {
    event.preventDefault();

    try {
      setIsCreating(true);
      setError("");
      const report = await api.createReport(formState);
      setMessage("Report created.");
      setFormState(createDefaultReport());
      navigate(`/reports/manual/${report.id}`);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsCreating(false);
    }
  }

  async function handleDelete(reportId) {
    if (!window.confirm("Delete this report and all of its findings?")) {
      return;
    }

    try {
      setError("");
      await api.deleteReport(reportId);
      setReports((currentReports) => currentReports.filter((report) => report.id !== reportId));
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  function updateField(field, value) {
    setFormState((currentState) => ({ ...currentState, [field]: value }));
  }

  return (
    <main className="manual-reports-scope manual-report-shell reports-shell">
      <section className="hero-card">
        <div>
          <p className="eyebrow">Manual & Hybrid Reports</p>
          <h1>Report Builder</h1>
          <p className="hero-copy">
            Draft polished security reports by hand, use AI to refine findings, and export clean deliverables without
            leaving the platform.
          </p>
        </div>
      </section>

      <section className="reports-layout">
        <form className="panel create-report-panel" onSubmit={handleCreateReport}>
          <div className="panel-header">
            <div>
              <p className="eyebrow">New Manual Report</p>
              <h2>Create Report</h2>
            </div>
            <button type="submit" className="primary-button" disabled={isCreating}>
              {isCreating ? "Creating..." : "Create Report"}
            </button>
          </div>

          <div className="report-grid">
            {Object.entries(DEFAULT_REPORT).map(([field, defaultValue]) => {
              const label = field
                .split("_")
                .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                .join(" ");
              const isLongField = ["scope_summary", "testing_notes", "methodology_notes", "report_notes"].includes(field);

              return (
                <label key={field} className={`field ${isLongField ? "field-wide" : ""}`}>
                  <span>{label}</span>
                  {isLongField ? (
                    <textarea rows={4} value={formState[field] || defaultValue} onChange={(event) => updateField(field, event.target.value)} />
                  ) : (
                    <input
                      type={field.includes("date") ? "date" : "text"}
                      value={formState[field] || defaultValue}
                      onChange={(event) => updateField(field, event.target.value)}
                    />
                  )}
                </label>
              );
            })}
          </div>
        </form>

        <section className="panel report-list-panel">
          <div className="panel-header">
            <div>
              <p className="eyebrow">Saved Manual Reports</p>
              <h2>Report Library</h2>
            </div>
          </div>

          {isLoading ? <p>Loading reports...</p> : null}
          {error ? <p className="status-error">{error}</p> : null}
          {message ? <p className="status-success">{message}</p> : null}

          <div className="report-card-list">
            {!isLoading && reports.length === 0 ? <p className="empty-state">No reports yet.</p> : null}
            {reports.map((report) => (
              <article key={report.id} className="report-card">
                <div>
                  <p className="report-card-title">{report.report_title}</p>
                  <p className="report-card-subtitle">{report.client_name}</p>
                  <p className="report-card-meta">{report.assessment_type}</p>
                </div>
                <div className="toolbar-row">
                  <button type="button" className="secondary-button" onClick={() => navigate(`/reports/manual/${report.id}`)}>
                    Open
                  </button>
                  <button type="button" className="ghost-button danger-button" onClick={() => handleDelete(report.id)}>
                    Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      </section>
    </main>
  );
}
